/**
 * A bank's mark: its logo when the API sent one, otherwise a monogram tile.
 *
 * Decorative (`alt=""`): the bank's name always sits next to it, so a screen
 * reader would only hear it twice.
 */
import * as React from "react";

import { cn } from "./cn";

type InstitutionLogoProps = {
  name: string | null;
  logoUrl: string | null;
  /** The bank's brand colour (a hex string); only used on the monogram. */
  color?: string | null;
  /** `xs` fits a grid row, `sm` sits inside a chip, `md` heads a row. */
  size?: "xs" | "sm" | "md";
  className?: string;
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const tileSize = { xs: "size-5", sm: "size-7", md: "size-9" } as const;
const imgSize = { xs: "size-3.5", sm: "size-5", md: "size-7" } as const;
const letterSize = { xs: "text-2xs", sm: "text-xs", md: "text-sm" } as const;

function InstitutionLogo({
  name,
  logoUrl,
  color,
  size = "md",
  className,
}: InstitutionLogoProps) {
  const [failedUrl, setFailedUrl] = React.useState<string | null>(null);
  if (logoUrl && failedUrl !== logoUrl) {
    return (
      <span
        className={cn(
          "flex shrink-0 items-center justify-center overflow-hidden rounded-control bg-card ring-1 ring-border",
          tileSize[size],
          className,
        )}
      >
        <img
          src={logoUrl}
          alt=""
          className={cn("object-contain", imgSize[size])}
          onError={() => setFailedUrl(logoUrl)}
        />
      </span>
    );
  }
  const letter = (name?.trim().charAt(0) || "B").toUpperCase();
  const style =
    color && HEX_COLOR.test(color)
      ? ({ "--bank-color": color } as React.CSSProperties)
      : undefined;
  return (
    <span
      aria-hidden="true"
      style={style}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-control bg-service-tint font-semibold text-service ring-1 ring-(--bank-color,transparent)",
        tileSize[size],
        letterSize[size],
        className,
      )}
    >
      {letter}
    </span>
  );
}

export { InstitutionLogo };
export type { InstitutionLogoProps };
